import { PDFDocument } from "pdf-lib";

export const MATERIAL_SOURCE_MAX_BYTES = 32 * 1024 * 1024;
export const MATERIAL_SOURCE_MAX_PAGES = 400;
const MAX_IMAGE_PIXELS = 80_000_000;

const checkCurrent = (isCurrent) => {
  if (isCurrent && !isCurrent()) {
    const error = new Error("已取消組合教材來源。");
    error.name = "AbortError";
    throw error;
  }
};

const kindOf = (file) => {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  if (type === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (["image/png", "image/jpeg", "image/webp"].includes(type) || /\.(png|jpe?g|webp)$/.test(name)) return "image";
  throw new Error(`「${file?.name || "未命名來源"}」格式不支援，請使用 PDF、PNG、JPG 或 WebP。`);
};

const readBytes = (blob) => {
  if (typeof blob.arrayBuffer === "function") return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("無法讀取來源檔案。"));
    reader.onabort = () => reject(Object.assign(new Error("已取消讀取來源。"), { name: "AbortError" }));
    reader.readAsArrayBuffer(blob);
  });
};

const loadPdf = async (bytes, filename) => {
  try {
    const pdf = await PDFDocument.load(bytes, { updateMetadata: false });
    if (pdf.getPageCount() < 1) throw new Error("來源沒有頁面。");
    return pdf;
  } catch (error) {
    throw new Error(`無法讀取「${filename}」PDF；請確認檔案完整並已解除密碼。`);
  }
};

// Only EXIF images need decoding; ordinary JPEG/PNG bytes retain their quality.
const jpegOrientation = (bytes) => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 2;
  try {
    while (offset + 4 <= bytes.length && bytes[offset] === 0xff) {
      const marker = bytes[offset + 1];
      if (marker === 0xda || marker === 0xd9) break;
      const size = view.getUint16(offset + 2);
      if (size < 2 || offset + 2 + size > bytes.length) break;
      if (marker === 0xe1 && view.getUint32(offset + 4) === 0x45786966 && view.getUint16(offset + 8) === 0) {
        const tiff = offset + 10;
        const little = view.getUint16(tiff) === 0x4949;
        if (view.getUint16(tiff + 2, little) !== 42) return 1;
        const directory = tiff + view.getUint32(tiff + 4, little);
        const count = view.getUint16(directory, little);
        for (let index = 0; index < count; index += 1) {
          const entry = directory + 2 + index * 12;
          if (view.getUint16(entry, little) === 0x0112) {
            const orientation = view.getUint16(entry + 8, little);
            return orientation >= 1 && orientation <= 8 ? orientation : 1;
          }
        }
      }
      offset += 2 + size;
    }
  } catch (error) {
    // Malformed metadata is not an instruction to rotate the source.
  }
  return 1;
};

const browserImage = async (blob) => {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
      return { image: bitmap, width: bitmap.width, height: bitmap.height, dispose: () => bitmap.close?.() };
    } catch (error) {
      // Safari versions without the bitmap options can use their normal decoder.
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const image = await new Promise((resolve, reject) => {
      const value = new Image();
      value.onload = () => resolve(value);
      value.onerror = () => reject(new Error("圖片解碼失敗，請重新選擇來源。"));
      value.src = url;
    });
    return { image, width: image.naturalWidth, height: image.naturalHeight,
      dispose: () => URL.revokeObjectURL(url) };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
};

const rasterPng = async (bytes, type, isCurrent) => {
  const decoded = await browserImage(new Blob([bytes], { type }));
  let canvas;
  try {
    checkCurrent(isCurrent);
    if (decoded.width <= 0 || decoded.height <= 0 || decoded.width * decoded.height > MAX_IMAGE_PIXELS) {
      throw new Error("圖片像素超過 8,000 萬或尺寸無效，請先縮小來源圖片。");
    }
    canvas = document.createElement("canvas");
    canvas.width = decoded.width;
    canvas.height = decoded.height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("瀏覽器無法建立圖片畫布。");
    context.drawImage(decoded.image, 0, 0, decoded.width, decoded.height);
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob((value) => value ? resolve(value) : reject(new Error("圖片轉換失敗。")), "image/png");
    });
    checkCurrent(isCurrent);
    const output = await readBytes(blob);
    checkCurrent(isCurrent);
    return output;
  } finally {
    decoded.dispose();
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
};

const embedImage = async (pdf, bytes, isCurrent) => {
  const imageBytes = new Uint8Array(bytes);
  let image;
  if (imageBytes[0] === 0x89 && imageBytes[1] === 0x50) {
    image = await pdf.embedPng(bytes);
  } else if (imageBytes[0] === 0xff && imageBytes[1] === 0xd8) {
    image = jpegOrientation(imageBytes) === 1
      ? await pdf.embedJpg(bytes)
      : await pdf.embedPng(await rasterPng(bytes, "image/jpeg", isCurrent));
  } else if (String.fromCharCode(...imageBytes.slice(0, 4)) === "RIFF"
      && String.fromCharCode(...imageBytes.slice(8, 12)) === "WEBP") {
    image = await pdf.embedPng(await rasterPng(bytes, "image/webp", isCurrent));
  } else {
    throw new Error("無法辨識圖片內容，請使用有效的 PNG、JPG 或 WebP。");
  }
  checkCurrent(isCurrent);
  if (image.width * image.height > MAX_IMAGE_PIXELS) throw new Error("圖片像素超過 8,000 萬，請先縮小來源圖片。");
  const page = pdf.addPage([image.width, image.height]);
  page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
};

const outputName = (file, count) => {
  const base = String(file?.name || "教材來源").replace(/\.(pdf|png|jpe?g|webp)$/i, "")
    .replace(/[\\/:*?"<>|\u0000-\u001f]/g, "-").trim();
  return `${Array.from(base || "教材來源").slice(0, 100).join("")}等${count}份.pdf`;
};

/** Combine complete sources in order. Printed question numbers are never keys. */
export const combineMaterialSources = async (files, { onProgress, isCurrent } = {}) => {
  const sources = Array.from(files || []);
  checkCurrent(isCurrent);
  if (!sources.length) return null;
  const kinds = sources.map((file) => {
    if (file.size > MATERIAL_SOURCE_MAX_BYTES) throw new Error("單份教材來源不可超過 32 MiB，請先分割來源。");
    return kindOf(file);
  });
  if (sources.length > MATERIAL_SOURCE_MAX_PAGES) throw new Error("組合教材不可超過 400 頁，請分批解析。");
  if (sources.length === 1) {
    if (kinds[0] === "pdf") {
      const bytes = await readBytes(sources[0]);
      checkCurrent(isCurrent);
      const original = await loadPdf(bytes, sources[0].name);
      checkCurrent(isCurrent);
      if (original.getPageCount() > MATERIAL_SOURCE_MAX_PAGES) throw new Error("教材不可超過 400 頁，請分批解析。");
    }
    return sources[0];
  }
  const pdf = await PDFDocument.create({ updateMetadata: false });
  checkCurrent(isCurrent);
  const provenance = [];
  onProgress?.({ current: 0, total: sources.length, filename: "", pageCount: 0 });
  for (let index = 0; index < sources.length; index += 1) {
    checkCurrent(isCurrent);
    const file = sources[index];
    const bytes = await readBytes(file);
    checkCurrent(isCurrent);
    const startPage = pdf.getPageCount() + 1;
    if (kinds[index] === "pdf") {
      const original = await loadPdf(bytes, file.name);
      checkCurrent(isCurrent);
      if (pdf.getPageCount() + original.getPageCount() > MATERIAL_SOURCE_MAX_PAGES) {
        throw new Error("組合教材不可超過 400 頁，請分批解析；來源未被截斷。");
      }
      const pages = await pdf.copyPages(original, original.getPageIndices());
      checkCurrent(isCurrent);
      pages.forEach((page) => pdf.addPage(page));
    } else {
      if (pdf.getPageCount() >= MATERIAL_SOURCE_MAX_PAGES) throw new Error("組合教材不可超過 400 頁，請分批解析。");
      await embedImage(pdf, bytes, isCurrent);
    }
    checkCurrent(isCurrent);
    provenance.push({ filename: file.name, start_page: startPage, end_page: pdf.getPageCount() });
    onProgress?.({ current: index + 1, total: sources.length, filename: file.name, pageCount: pdf.getPageCount() });
  }
  checkCurrent(isCurrent);
  pdf.setSubject(JSON.stringify({ schema_version: 1, kind: "material-source-pages", files: provenance }));
  // Stable metadata keeps the same ordered sources byte-identical after refresh,
  // so an unknown upload can recover its existing client receipt safely.
  pdf.setCreationDate(new Date("2000-01-01T00:00:00Z"));
  pdf.setModificationDate(new Date("2000-01-01T00:00:00Z"));
  const output = await pdf.save();
  checkCurrent(isCurrent);
  if (output.byteLength > MATERIAL_SOURCE_MAX_BYTES) throw new Error("組合後的 PDF 超過 32 MiB，請分批解析；來源未被截斷。");
  return new File([output], outputName(sources[0], sources.length), { type: "application/pdf", lastModified: 0 });
};
